import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { X, ArrowRight, Loader2, ShieldCheck, MessageCircle } from "lucide-react";
import { trackWhatsApp } from "../../lib/beacon";

/**
 * Modal de triagem rápida — 5 perguntas antes de abrir WhatsApp do consultor.
 *
 * Objetivo: capturar contexto mínimo (nome, para quem, desafios, motivação,
 * horário) → IA formata mensagem humana → família é redirecionada pro wa.me
 * com o texto já pronto. Consultor abre o WhatsApp com o caminho inicial
 * completo, sem precisar interrogar a família do zero.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   targetKind  'prof' | 'clinic'
 *   targetId    string
 *   targetLabel string (nome do profissional/clínica)
 */

const CONCERN_CHIPS = [
  "Ansiedade", "Depressão", "Dependência química", "Álcool",
  "Crise familiar", "Adolescente em risco", "Idoso", "Transtornos alimentares",
  "Ideação suicida", "Outro",
];
const FOR_WHOM = [
  { k: "self",     label: "Para mim mesmo(a)" },
  { k: "child",    label: "Para meu(minha) filho(a)" },
  { k: "relative", label: "Para um(a) familiar" },
  { k: "friend",   label: "Para um(a) amigo(a)" },
];
const BEST_TIME = [
  { k: "now",       label: "Agora mesmo" },
  { k: "morning",   label: "Manhã" },
  { k: "afternoon", label: "Tarde" },
  { k: "evening",   label: "Noite" },
];

const QuickTriageModal = ({ open, onClose, targetKind, targetId, targetLabel }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", for_whom: "self",
    age: "", concerns: [], reason: "", best_time: "now",
  });

  if (!open) return null;

  const toggleConcern = (c) => setForm((f) => ({
    ...f,
    concerns: f.concerns.includes(c) ? f.concerns.filter((x) => x !== c) : [...f.concerns, c],
  }));

  const canNext = () => {
    if (step === 1) return form.name.trim().length >= 2;
    if (step === 2) return form.for_whom && (form.for_whom === "self" || form.age.trim().length > 0);
    if (step === 3) return form.concerns.length > 0;
    return true;
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        target_kind: targetKind, target_id: targetId, target_label: targetLabel,
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        for_whom: form.for_whom,
        age: form.age.trim() || null,
        concerns: form.concerns,
        reason: form.reason.trim() || null,
        best_time: form.best_time,
      };
      const r = await api.post("/quick-triage/submit", payload);
      trackWhatsApp(`triage_${targetKind}`, { target_id: targetId, triage_id: r.data.triage_id });
      toast.success("Triagem enviada — abrindo WhatsApp…");
      // Abre wa.me em nova aba com a mensagem já pronta.
      window.open(r.data.wa_url, "_blank", "noopener,noreferrer");
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erro ao enviar triagem — tente novamente";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const dot = (n) => (
    <div className={`h-1.5 w-8 rounded-full ${step >= n ? "bg-emerald-600" : "bg-slate-200"}`} />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
         onClick={onClose}
         data-testid="quick-triage-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Triagem sigilosa · 30 segundos
            </div>
            <h2 className="font-display font-bold text-[#0B2B62] text-lg mt-0.5">
              Falar com consultor sobre {targetLabel}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="triage-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-5 pt-3 flex gap-1.5">{dot(1)}{dot(2)}{dot(3)}{dot(4)}{dot(5)}</div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          {step === 1 && (
            <div className="space-y-3">
              <label className="block">
                <div className="text-xs font-semibold text-slate-700 mb-1">Como você se chama? *</div>
                <input value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Seu nome completo"
                  autoFocus data-testid="triage-name"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none" />
              </label>
              <label className="block">
                <div className="text-xs font-semibold text-slate-700 mb-1">Telefone (opcional — só se preferir que o consultor ligue)</div>
                <input value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(11) 9 xxxx-xxxx" data-testid="triage-phone"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none" />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="text-xs font-semibold text-slate-700">Para quem é o atendimento? *</div>
              <div className="grid grid-cols-2 gap-2">
                {FOR_WHOM.map((o) => (
                  <button key={o.k} onClick={() => setForm({ ...form, for_whom: o.k })}
                    data-testid={`triage-forwhom-${o.k}`}
                    className={`text-xs px-3 py-2.5 rounded-md border-2 text-left transition-colors ${form.for_whom === o.k ? "border-emerald-600 bg-emerald-50 text-emerald-900 font-bold" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {form.for_whom !== "self" && (
                <label className="block">
                  <div className="text-xs font-semibold text-slate-700 mb-1">Qual a idade da pessoa? *</div>
                  <input value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    placeholder="Ex: 16 anos" data-testid="triage-age"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none" />
                </label>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-slate-700">O que está enfrentando agora? *</div>
              <div className="text-[10px] text-slate-500 -mt-1">Selecione todos que se aplicam.</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {CONCERN_CHIPS.map((c) => {
                  const on = form.concerns.includes(c);
                  return (
                    <button key={c} onClick={() => toggleConcern(c)}
                      data-testid={`triage-concern-${c.replace(/[^a-z]+/gi, "-")}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-emerald-600 border-emerald-700 text-white font-bold" : "bg-white border-slate-300 text-slate-700 hover:border-emerald-400"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <label className="block">
              <div className="text-xs font-semibold text-slate-700 mb-1">O que te levou a escolher {targetLabel}? <span className="text-slate-400 font-normal">(opcional)</span></div>
              <textarea value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Ex: procurei um psiquiatra que também atenda adolescente e vi o perfil dele com selo verificado."
                rows={4} maxLength={500} data-testid="triage-reason"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none resize-none" />
              <div className="text-[10px] text-slate-400 text-right mt-1">{form.reason.length}/500</div>
            </label>
          )}

          {step === 5 && (
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Melhor horário para o consultor te procurar? *</div>
              <div className="grid grid-cols-2 gap-2">
                {BEST_TIME.map((o) => (
                  <button key={o.k} onClick={() => setForm({ ...form, best_time: o.k })}
                    data-testid={`triage-time-${o.k}`}
                    className={`text-xs px-3 py-2.5 rounded-md border-2 text-left transition-colors ${form.best_time === o.k ? "border-emerald-600 bg-emerald-50 text-emerald-900 font-bold" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2">
                <MessageCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Ao enviar, você será redirecionado(a) para o WhatsApp do consultor RAF de plantão com um resumo do que você respondeu. Rápido, sigiloso e sem cadastro.</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            data-testid="triage-back"
            className="text-xs text-slate-500 hover:text-slate-700 px-3 py-2">
            {step === 1 ? "Cancelar" : "← Voltar"}
          </button>
          {step < 5 ? (
            <button onClick={() => canNext() && setStep(step + 1)}
              disabled={!canNext()} data-testid="triage-next"
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-40 disabled:cursor-not-allowed">
              Próximo <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button onClick={submit} disabled={busy}
              data-testid="triage-submit"
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-40">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</> : <><MessageCircle className="w-3.5 h-3.5" /> Enviar e abrir WhatsApp</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickTriageModal;
